import parsePhoneNumberFromString, {
  AsYouType,
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  validatePhoneNumberLength,
} from 'libphonenumber-js';

export type PhoneCountryOption = {
  iso: CountryCode;
  dial: string;
  flag: string;
  label: string;
};

let cachedOptions: PhoneCountryOption[] | null = null;

export function flagEmoji(iso2: string): string {
  const u = iso2.toUpperCase();
  if (u.length !== 2) {
    return '🌐';
  }
  return String.fromCodePoint(...[...u].map((c) => 127397 + (c.codePointAt(0) ?? 0)));
}

/** Sorted country list with flag + dial code — built once (lazy). */
export function getPhoneCountryOptions(): PhoneCountryOption[] {
  if (cachedOptions) {
    return cachedOptions;
  }
  const dn = new Intl.DisplayNames(['en'], { type: 'region' });
  cachedOptions = getCountries()
    .map((iso) => {
      return {
        iso,
        dial: '+' + getCountryCallingCode(iso),
        flag: flagEmoji(iso),
        label: dn.of(iso) ?? iso,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
  return cachedOptions;
}

/** Maps a dial code (e.g. `91` or `+91`) to an ISO country when unambiguous or via platform preference. */
export function countryIsoFromDialCode(dialCode: string | null | undefined): CountryCode | null {
  const dialDigits = (dialCode ?? '').replace(/\D/g, '');
  if (!dialDigits) {
    return null;
  }
  return preferIsoForDial(dialDigits);
}

/** Client contact country: stored ISO wins; otherwise infer from the contact dial code. */
export function resolveTenantContactCountryIso(
  storedIso: string | null | undefined,
  dialCode: string | null | undefined,
): CountryCode | null {
  const fromDb = normalizeStoredCountryIso(storedIso);
  if (fromDb) {
    return fromDb;
  }
  return countryIsoFromDialCode(dialCode);
}

function preferIsoForDial(dialDigits: string): CountryCode | null {
  const matches = getCountries().filter((c) => getCountryCallingCode(c) === dialDigits);
  if (matches.length === 0) {
    return null;
  }
  if (matches.length === 1) {
    return matches[0];
  }
  const prefer: CountryCode[] = ['IN', 'US', 'GB', 'AU', 'CA'];
  for (const p of prefer) {
    if (matches.includes(p)) {
      return p;
    }
  }
  return matches[0];
}

/** Validate and normalize API-persisted ISO (e.g. US, CA). */
export function normalizeStoredCountryIso(raw: string | null | undefined): CountryCode | null {
  const s = raw?.trim().toUpperCase();
  if (s?.length !== 2) {
    return null;
  }
  if (getCountries().includes(s as CountryCode)) {
    return s as CountryCode;
  }
  return null;
}

/**
 * When several countries share a dial code (+1), prefer the ISO stored on the tenant row
 * if it matches that dial code; otherwise fall back to dial inference.
 */
function resolveCountryIso(
  dialDigits: string,
  isoFromDial: CountryCode | null,
  storedCountryIso: string | null | undefined,
  defaultIso: CountryCode,
): CountryCode {
  const fromDb = normalizeStoredCountryIso(storedCountryIso);
  if (fromDb) {
    if (!dialDigits || getCountryCallingCode(fromDb) === dialDigits) {
      return fromDb;
    }
  }
  return isoFromDial ?? defaultIso;
}

export function normalizeNationalDigits(raw: string): string {
  return raw.replace(/\D/g, '');
}

/** India mobile: 10 digits, leading 6–9 (optional leading 0 stripped). */
const IN_MOBILE_RE = /^[6-9]\d{9}$/;

/** Max national digits allowed while typing (India = 10). */
export function maxNationalDigitCount(countryIso: CountryCode): number | null {
  if (countryIso === 'IN') {
    return 10;
  }
  return null;
}

/**
 * Whether these digits are longer than the country allows.
 *
 * maxNationalDigitCount only ever knew about India, so every other country could be typed or
 * pasted to any length and was only told at submit. This asks the library, which knows the
 * possible lengths per country — including the ones that vary. India keeps its explicit 10:
 * libphonenumber accepts 11 digits there, and the exact rule is the useful one at the keyboard.
 */
export function exceedsNationalLength(countryIso: CountryCode, digits: string): boolean {
  if (!digits) return false;
  const explicit = maxNationalDigitCount(countryIso);
  if (explicit != null) return digits.length > explicit;
  try {
    return validatePhoneNumberLength(digits, countryIso) === 'TOO_LONG';
  } catch {
    // E.164 allows 15 digits at most, so nothing sensible is longer than this.
    return digits.length > 15;
  }
}

/** Digits trimmed to what the country allows, for a paste that is too long. */
export function clampNationalDigits(countryIso: CountryCode, digits: string): string {
  let out = digits;
  while (out.length > 1 && exceedsNationalLength(countryIso, out)) {
    out = out.slice(0, -1);
  }
  return out;
}

/**
 * Splits a pasted international number into country and national parts.
 *
 * autocomplete="tel" and a copied contact both hand over "+91 98765 43210". Kept whole, the
 * country code counts as national digits and a real number reads as invalid.
 */
export function splitInternationalNumber(
  value: string,
): { iso: CountryCode; national: string } | null {
  if (!value?.trim().startsWith('+')) return null;
  const parsed = parsePhoneNumberFromString(value.trim());
  if (!parsed?.country) return null;
  return { iso: parsed.country, national: parsed.nationalNumber };
}

/** Normalize and cap Indian national digits to at most 10. */
export function normalizeIndianMobileDigits(raw: string): string {
  let d = normalizeNationalDigits(raw);
  if (d.length === 11 && d.startsWith('0')) {
    d = d.slice(1);
  }
  if (d.length > 10) {
    d = d.slice(0, 10);
  }
  return d;
}

/**
 * Apply national formatting for display.
 * India (+IN): all 10-digit nationals use one pattern (5 + 5) — avoids mixed AsYouType layouts (e.g. 4-3-3 vs 5-5).
 * Other countries: AsYouType on full digits; partial input uses progressive AsYouType.
 */
export function formatPhoneNational(countryIso: CountryCode, raw: string): string {
  const digits = clampNationalDigits(countryIso, normalizeNationalDigits(raw));
  if (!digits) {
    return '';
  }

  if (countryIso === 'IN') {
    const d = normalizeIndianMobileDigits(raw);
    if (!d) {
      return '';
    }
    if (d.length === 10) {
      return `${d.slice(0, 5)} ${d.slice(5)}`;
    }
    const ayt = new AsYouType('IN');
    return ayt.input(d);
  }

  const pn = parsePhoneNumberFromString(digits, countryIso);
  if (pn?.isValid()) {
    const ayt = new AsYouType(countryIso);
    return ayt.input(pn.nationalNumber);
  }

  const ayt = new AsYouType(countryIso);
  return ayt.input(digits);
}

/** Validate national digits for a country; empty input is allowed (optional fields). */
export function validateNationalPhone(countryIso: CountryCode, nationalRaw: string): string | null {
  const digits = normalizeNationalDigits(nationalRaw);
  if (!digits) {
    return null;
  }
  if (countryIso === 'IN') {
    const d = normalizeIndianMobileDigits(nationalRaw);
    if (!IN_MOBILE_RE.test(d)) {
      return 'Enter a valid 10-digit mobile number.';
    }
    return null;
  }
  const pn = parsePhoneNumberFromString(digits, countryIso);
  if (!pn?.isValid()) {
    return 'Enter a valid phone number for the selected country.';
  }
  return null;
}

export function dialStringForCountry(countryIso: CountryCode): string {
  return '+' + getCountryCallingCode(countryIso);
}

/**
 * Map API fields into ISO + national digits for the form.
 * Handles legacy values where full E.164 was stored in the national field.
 * @param storedCountryIso — persisted ISO (US/CA/…) when dial code alone is ambiguous (e.g. +1).
 */
export function splitPhoneForForm(
  storedCountryCode: string | null | undefined,
  storedNational: string | null | undefined,
  defaultIso: CountryCode = 'IN',
  storedCountryIso?: string | null | undefined,
): { iso: CountryCode; national: string } {
  const cc = (storedCountryCode ?? '').trim();
  const nat = (storedNational ?? '').trim();
  const dialDigits = cc.replace(/^\+/, '').replace(/\D/g, '');
  const isoFromCc = dialDigits ? preferIsoForDial(dialDigits) : null;

  if (!nat && !cc) {
    return { iso: defaultIso, national: '' };
  }

  if (nat.startsWith('+')) {
    const pn = parsePhoneNumberFromString(nat);
    if (pn?.isValid() && pn.country) {
      const iso = pn.country;
      const formatted = formatPhoneNational(iso, pn.nationalNumber);
      return { iso, national: formatted };
    }
  }

  const nationalDigits = normalizeNationalDigits(nat);
  const iso = resolveCountryIso(dialDigits, isoFromCc, storedCountryIso, defaultIso);

  if (nationalDigits) {
    const pn = parsePhoneNumberFromString(nationalDigits, iso);
    if (pn?.isValid()) {
      const formatted = formatPhoneNational(iso, pn.nationalNumber);
      return { iso, national: formatted };
    }
  }

  const nationalFormatted = nationalDigits ? formatPhoneNational(iso, nationalDigits) : '';
  return { iso, national: nationalFormatted };
}

/**
 * Read-only display (tables, CSV previews): **+country code** + **national spacing** using the same rules as
 * {@link formatPhoneNational} on add/edit (`app-intl-phone-field`). Avoids `formatInternational()` from
 * libphonenumber alone, which can differ from form spacing (e.g. India 5+5 vs 3-2-3-2).
 */
export function formatPhoneInternationalDisplay(
  storedNational: string | null | undefined,
  storedCountryCode: string | null | undefined,
  storedCountryIso: string | null | undefined,
  defaultIso: CountryCode = 'IN',
): string {
  const raw = (storedNational ?? '').trim();
  if (!raw) {
    return '—';
  }

  if (raw.startsWith('+')) {
    const pn = parsePhoneNumberFromString(raw);
    if (pn?.country) {
      const iso = pn.country;
      const natFmt = formatPhoneNational(iso, pn.nationalNumber);
      return `${dialStringForCountry(iso)} ${natFmt}`.trim();
    }
    return raw;
  }

  const { iso, national } = splitPhoneForForm(
    storedCountryCode,
    raw,
    defaultIso,
    storedCountryIso,
  );

  const digits = normalizeNationalDigits(national || raw);
  if (!digits) {
    return '—';
  }

  const natFmt = formatPhoneNational(iso, digits);
  return `${dialStringForCountry(iso)} ${natFmt}`.trim();
}
